import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Users, Activity, Calendar, RefreshCw, Lock, TrendingUp, Mail, ChevronDown, ChevronUp,
  Gift, X as XIcon
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [showUsers, setShowUsers] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Comps section state
  const [compEmail, setCompEmail] = useState('');
  const [compDuration, setCompDuration] = useState(365);
  const [compNote, setCompNote] = useState('');
  const [granting, setGranting] = useState(false);
  const [compsList, setCompsList] = useState({ active_comps: [], pending_comps: [] });
  const [showComps, setShowComps] = useState(false);

  const getAuthHeader = () => ({ Authorization: `Bearer ${token}` });

  // Check admin status on mount
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/check`, {
          headers: getAuthHeader()
        });
        if (res.data.is_admin) {
          setIsAdmin(true);
          fetchStats();
        } else {
          setIsAdmin(false);
          setLoading(false);
        }
      } catch (error) {
        setIsAdmin(false);
        setLoading(false);
      }
    };
    checkAdmin();
  }, [token]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/stats`, {
        headers: getAuthHeader()
      });
      setStats(res.data);
    } catch (error) {
      toast.error('Failed to load admin stats');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/users`, {
        headers: getAuthHeader()
      });
      setUsers(res.data.users || []);
    } catch (error) {
      toast.error('Failed to load user list');
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchStats();
    if (showUsers) fetchUsers();
  };

  const toggleUserList = () => {
    if (!showUsers && users.length === 0) {
      fetchUsers();
    }
    setShowUsers(!showUsers);
  };

  // ---- Comp handlers ----
  const fetchComps = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/comps/list`, { headers: getAuthHeader() });
      setCompsList(res.data);
    } catch (error) {
      toast.error('Failed to load comps list');
    }
  };

  const toggleComps = () => {
    if (!showComps) fetchComps();
    setShowComps(!showComps);
  };

  const handleGrantComp = async () => {
    const email = compEmail.trim();
    if (!email) {
      toast.error('Enter an email address');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      toast.error('That email doesn\'t look right');
      return;
    }
    if (compDuration <= 0 || compDuration > 3650) {
      toast.error('Duration must be between 1 and 3650 days');
      return;
    }
    setGranting(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/comps/grant`,
        { email, duration_days: parseInt(compDuration, 10), note: compNote.trim() || undefined },
        { headers: getAuthHeader() }
      );
      const suffix = res.data.applied === 'immediate'
        ? `Applied immediately — expires ${new Date(res.data.subscription_end).toLocaleDateString()}`
        : `Queued as pending — will auto-apply when ${email} signs up`;
      toast.success(`Comp granted to ${email}. ${suffix}`);
      setCompEmail('');
      setCompNote('');
      if (showComps) fetchComps();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Grant failed';
      toast.error(msg);
    } finally {
      setGranting(false);
    }
  };

  const handleRevokeComp = async (email) => {
    if (!window.confirm(`Revoke comp for ${email}? They\u2019ll drop back to trial status on their next request.`)) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/comps/revoke`,
        { email },
        { headers: getAuthHeader() }
      );
      toast.success(`Comp revoked for ${email}`);
      fetchComps();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Revoke failed');
    }
  };

  // Not admin - show access denied
  if (!loading && !isAdmin) {
    return (
      <div className="p-4 max-w-2xl mx-auto">
        <Card className="card-farm">
          <CardContent className="p-8 text-center">
            <Lock className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-heading font-bold text-foreground mb-2">
              Access Denied
            </h2>
            <p className="text-muted-foreground mb-4">
              This page is only accessible to administrators.
            </p>
            <Button onClick={() => navigate('/app')} variant="outline">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="p-4 max-w-2xl mx-auto">
        <Card className="card-farm">
          <CardContent className="p-8 text-center">
            <RefreshCw className="w-8 h-8 mx-auto text-primary animate-spin mb-4" />
            <p className="text-muted-foreground">Loading admin dashboard...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground">
            Admin Dashboard
          </h1>
          <p className="text-sm text-muted-foreground">
            Fencepost usage statistics
          </p>
        </div>
        <Button 
          onClick={handleRefresh} 
          variant="outline" 
          size="sm"
          disabled={refreshing}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        {/* Total Users */}
        <Card className="card-farm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Users className="w-4 h-4" />
              Total Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-foreground">
              {stats?.total_users || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Registered accounts
            </p>
          </CardContent>
        </Card>

        {/* Users with Records */}
        <Card className="card-farm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Activity className="w-4 h-4" />
              Logged Records
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-foreground">
              {stats?.users_with_records || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              <span className="text-primary font-medium">
                {stats?.users_with_records_pct || 0}%
              </span>
              {' '}of total users
            </p>
          </CardContent>
        </Card>

        {/* Active Users (3+ days) */}
        <Card className="card-farm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Active 3+ Days
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-foreground">
              {stats?.users_three_plus_days || 0}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              <span className="text-primary font-medium">
                {stats?.users_three_plus_days_pct || 0}%
              </span>
              {' '}of total users
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Engagement Funnel */}
      <Card className="card-farm">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            User Engagement Funnel
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Funnel visualization */}
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Registered Users</span>
                  <span className="font-medium">{stats?.total_users || 0}</span>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full" style={{ width: '100%' }} />
                </div>
              </div>
              
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Logged at least 1 record</span>
                  <span className="font-medium">
                    {stats?.users_with_records || 0} ({stats?.users_with_records_pct || 0}%)
                  </span>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 rounded-full transition-all" 
                    style={{ width: `${stats?.users_with_records_pct || 0}%` }} 
                  />
                </div>
              </div>
              
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Active on 3+ days</span>
                  <span className="font-medium">
                    {stats?.users_three_plus_days || 0} ({stats?.users_three_plus_days_pct || 0}%)
                  </span>
                </div>
                <div className="h-3 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-amber-500 rounded-full transition-all" 
                    style={{ width: `${stats?.users_three_plus_days_pct || 0}%` }} 
                  />
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User List */}
      <Card className="card-farm">
        <CardHeader className="cursor-pointer" onClick={toggleUserList}>
          <CardTitle className="font-heading text-lg flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-primary" />
              User List
            </div>
            <Button variant="ghost" size="sm">
              {showUsers ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </CardTitle>
        </CardHeader>
        {showUsers && (
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Email</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Farm</th>
                    <th className="text-center py-2 px-2 font-medium text-muted-foreground">Records</th>
                    <th className="text-center py-2 px-2 font-medium text-muted-foreground">Days</th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u, idx) => (
                    <tr key={u.id || idx} className="border-b border-muted/50 hover:bg-muted/20">
                      <td className="py-2 px-2 font-medium">{u.email}</td>
                      <td className="py-2 px-2 text-muted-foreground">{u.farm_name || '-'}</td>
                      <td className="py-2 px-2 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          u.record_count > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {u.record_count}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${
                          u.active_days >= 3 ? 'bg-amber-100 text-amber-700' : 
                          u.active_days > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {u.active_days}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-muted-foreground text-xs">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length === 0 && (
                <p className="text-center text-muted-foreground py-4">Loading users...</p>
              )}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Comps Management */}
      <Card className="card-farm" data-testid="admin-comps-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Gift className="w-4 h-4 text-emerald-700" /> Grant Free Comps
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            Grant a time-bounded free subscription. Works for existing users AND emails that haven&apos;t
            signed up yet (auto-applies at first registration).
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-muted-foreground block mb-1">Email address</label>
              <Input
                type="email"
                placeholder="tester@example.com"
                value={compEmail}
                onChange={(e) => setCompEmail(e.target.value)}
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                data-testid="comp-email-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Duration (days)</label>
              <Input
                type="number"
                min="1"
                max="3650"
                value={compDuration}
                onChange={(e) => setCompDuration(e.target.value)}
                data-testid="comp-duration-input"
              />
              <p className="text-xs text-muted-foreground mt-1">365 = 1 year</p>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">Note (optional)</label>
              <Input
                placeholder="Closed test comp"
                value={compNote}
                onChange={(e) => setCompNote(e.target.value)}
                data-testid="comp-note-input"
              />
            </div>
          </div>
          <Button
            onClick={handleGrantComp}
            disabled={granting}
            className="w-full sm:w-auto"
            data-testid="comp-grant-btn"
          >
            {granting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Gift className="w-4 h-4 mr-2" />}
            Grant Comp
          </Button>

          <div className="pt-3 border-t">
            <Button
              onClick={toggleComps}
              variant="ghost"
              size="sm"
              className="w-full justify-between"
              data-testid="comps-toggle-btn"
            >
              <span className="text-sm">View existing comps</span>
              {showComps ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>

          {showComps && (
            <div className="space-y-4 pt-2" data-testid="comps-list">
              <div>
                <h4 className="text-sm font-semibold mb-2">
                  Active comps ({compsList.active_comps?.length || 0})
                </h4>
                {(compsList.active_comps || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">No active comps yet.</p>
                ) : (
                  <ul className="divide-y">
                    {compsList.active_comps.map((c) => (
                      <li key={c.email} className="py-2 flex flex-wrap items-center gap-2">
                        <div className="flex-1 min-w-0 basis-full sm:basis-auto">
                          <div className="text-sm font-medium truncate">{c.email}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            expires {c.subscription_end ? new Date(c.subscription_end).toLocaleDateString() : '—'}
                            {c.subscription_note ? ` · ${c.subscription_note}` : ''}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRevokeComp(c.email)}
                          data-testid={`comp-revoke-${c.email}`}
                        >
                          <XIcon className="w-3.5 h-3.5 mr-1" /> Revoke
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <h4 className="text-sm font-semibold mb-2">
                  Pending comps ({compsList.pending_comps?.length || 0})
                </h4>
                {(compsList.pending_comps || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">No pending comps waiting.</p>
                ) : (
                  <ul className="divide-y">
                    {compsList.pending_comps.map((p) => (
                      <li key={p.email} className="py-2 flex flex-wrap items-center gap-2">
                        <div className="flex-1 min-w-0 basis-full sm:basis-auto">
                          <div className="text-sm font-medium truncate">{p.email}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {p.duration_days} days · queued {p.created_at ? new Date(p.created_at).toLocaleDateString() : ''}
                            {p.note ? ` · ${p.note}` : ''}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRevokeComp(p.email)}
                          data-testid={`pending-revoke-${p.email}`}
                        >
                          <XIcon className="w-3.5 h-3.5 mr-1" /> Cancel
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Last Updated */}
      {stats?.generated_at && (
        <p className="text-xs text-center text-muted-foreground">
          Last updated: {new Date(stats.generated_at).toLocaleString()}
        </p>
      )}
    </div>
  );
};

export default AdminDashboard;
